<template>
  <div class="mapbox-address-container">
    <div class="address-input-container">
      <AppLabel content="Project location" required class="d--b mb-20px" for="location-input" />
      <div class="autocomplete-container">
        <AppInput id="location-input" v-model="inputAddress" placeholder="Enter address" @input="handleAddressInput" />
        <div v-if="suggestions.length > 0" class="suggestions-dropdown">
          <div
            v-for="(suggestion, index) in suggestions"
            :key="index"
            class="suggestion-item"
            @click="selectSuggestion(suggestion)"
          >
            {{ suggestion.place_name }}
          </div>
        </div>
      </div>
      <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
    </div>

    <div v-if="showMap" class="map-section">
      <div class="boundary-requirement">
        <AppLabel content="Property boundary" required class="d--b mb-10px" />
        <div class="d--f ai--c">
          <p class="font-14px text-muted f--1">
            Draw the property boundary on the map, including all adjacent areas and empty spaces.
          </p>
          <InfoTooltip
            title="How to properly outline the property"
            description="For accurate calculation, include ALL property areas in the outline, including undeveloped territories and empty spaces. Draw a polygon that completely encompasses the entire property area."
            :imageUrl="propertyBoundaryImage"
          />
        </div>
      </div>
      <div class="map-container">
        <div id="map" ref="mapContainer"></div>
      </div>
      <div class="map-controls-container">
        <div class="map-controls">
          <AppButton @click="toggleDrawingMode" :variant="isDrawingMode ? 'outlined' : 'primary'" size="small">
            {{ isDrawingMode ? "Stop Drawing" : "Draw property boundary" }}
          </AppButton>
          <AppButton v-if="hasPolygon" @click="clearPolygon" variant="outlined" size="small"> Clear boundary </AppButton>
        </div>
        <div v-if="propertyArea > 0" class="property-area">
          <span>Property Area: {{ formatArea(propertyArea) }}</span>
        </div>
      </div>
      <span v-if="!hasPolygon && showBoundaryError" class="error-message">Property boundary is required</span>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";
import mapboxgl from "mapbox-gl";
import mapboxWorkerUrl from "mapbox-gl/dist/mapbox-gl-csp-worker.js?url";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { AppButton, AppInput, AppLabel } from "@/components/ui";
import * as turf from "@turf/turf";
import { useReportFormStore } from "@/stores/reportFormStore";
import InfoTooltip from "@/components/InfoTooltip.vue";
import propertyBoundaryImage from "@/assets/images/tooltips/map.png";

import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

// Configure Mapbox worker URL so the worker is served from the correct asset path
mapboxgl.workerUrl = mapboxWorkerUrl;

// Use the Pinia store
const store = useReportFormStore();

// Refs
const mapContainer = ref(null);
const map = ref(null);
const draw = ref(null);
const errorMessage = ref("");
const showMap = ref(false);
const isDrawingMode = ref(false);
const hasPolygon = ref(false);
const showBoundaryError = ref(false);
const inputAddress = ref(""); // For the input field
const suggestions = ref([]); // For address suggestions
const searchTimeout = ref(null); // For debouncing address search
const debugData = ref({
  geocoderResult: null,
  address: {
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
  },
  coordinates: {
    latitude: null,
    longitude: null,
  },
  mapStatus: {
    initialized: false,
    style: "mapbox://styles/mapbox/satellite-streets-v12",
    zoom: 15,
  },
  propertyBoundary: null,
  errors: [],
});

// Mapbox token from environment
const mapboxToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

// Watch showMap changes to initialize map when container is available
watch(showMap, (newVal) => {
  if (newVal && store.formData.latitude && store.formData.longitude) {
    // Use nextTick to ensure the DOM is updated before initializing the map
    nextTick().then(() => {
      if (mapContainer.value && !map.value) {
        initializeMap([store.formData.longitude, store.formData.latitude]);
      }
    });
  }
});

// Watch for clicks outside the dropdown to close it
const closeDropdownOnClickOutside = (event) => {
  const autocompleteContainer = document.querySelector(".autocomplete-container");
  if (autocompleteContainer && !autocompleteContainer.contains(event.target)) {
    suggestions.value = [];
  }
};

// Initialize the map
onMounted(() => {
  if (!mapboxToken) {
    errorMessage.value = "Mapbox access token not configured";
    console.error("Missing Mapbox access token");
    return;
  }

  mapboxgl.accessToken = mapboxToken;

  // If we already have coordinates, initialize the map
  if (store.formData.latitude && store.formData.longitude) {
    showMap.value = true;

    // Wait for DOM update before initializing map
    nextTick().then(() => {
      if (mapContainer.value) {
        initializeMap([store.formData.longitude, store.formData.latitude]);
      }
    });
  }

  // If we already have an address, set the input value
  if (store.formData.project_location) {
    inputAddress.value = store.formData.project_location;
  }

  // Add click event listener to close dropdown when clicking outside
  document.addEventListener("click", closeDropdownOnClickOutside);
});

// Clean up on unmount
onUnmounted(() => {
  if (map.value) {
    map.value.remove();
  }

  // Clear any pending timeouts
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  // Remove event listeners
  document.removeEventListener("click", closeDropdownOnClickOutside);
});

// Handle address input with debounce to fetch suggestions
const handleAddressInput = () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }

  searchTimeout.value = setTimeout(() => {
    if (inputAddress.value.length > 2) {
      fetchSuggestions();
    } else {
      suggestions.value = [];
    }
  }, 300);
};

// Fetch address suggestions from Mapbox
const fetchSuggestions = async () => {
  if (!inputAddress.value || inputAddress.value.length < 3) return;

  try {
    // Clear any previous error
    errorMessage.value = "";

    const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(inputAddress.value)}.json`;
    const params = new URLSearchParams({
      access_token: mapboxToken,
      types: "address",
      limit: 5,
      country: "ca,us",
    });

    const response = await fetch(`${endpoint}?${params.toString()}`);
    const data = await response.json();

    if (data.features && data.features.length > 0) {
      suggestions.value = data.features;
    } else {
      suggestions.value = [];
    }
  } catch (error) {
    console.error("Geocoding error:", error);
    errorMessage.value = `Geocoding error: ${error.message}`;
    debugData.value.errors.push(`Geocoder error: ${error.message}`);
    suggestions.value = [];
  }
};

// Select a suggestion from the dropdown
const selectSuggestion = (suggestion) => {
  // Close the dropdown
  suggestions.value = [];

  // Set the input value to the selected suggestion's place name
  inputAddress.value = suggestion.place_name;

  // Process the selected address
  processSelectedAddress(suggestion);
};

// Process the selected address
const processSelectedAddress = (result) => {
  // Store the full result in debug data
  debugData.value.geocoderResult = result;

  // Extract address components
  const addressParts = parseAddress(result);

  // Update debug data with parsed address
  debugData.value.address = addressParts;
  debugData.value.coordinates = {
    latitude: result.center[1],
    longitude: result.center[0],
  };

  // Create mapbox data to update the store
  const mapboxData = {
    street_address: addressParts.street_address,
    city: addressParts.city,
    state: addressParts.state,
    zip_code: addressParts.zip_code,
    latitude: result.center[1],
    longitude: result.center[0],
    property_boundary: null,
    project_location: result.place_name,
    property_area_m2: null,
  };

  // Update the store
  store.updateMapboxData(mapboxData);

  // Show the map after we have coordinates
  showMap.value = true;

  // Initialize map after DOM has updated
  nextTick().then(() => {
    if (!map.value && mapContainer.value) {
      initializeMap(result.center);
    } else if (map.value) {
      // Update map position
      map.value.flyTo({
        center: result.center,
        zoom: 15,
      });
    }
  });
};

// Initialize the map
const initializeMap = (center) => {
  // Wait for the mapContainer to be available in the DOM
  if (!mapContainer.value) {
    console.error("Map container element not found");
    debugData.value.errors.push("Map container element not found");
    return;
  }

  try {
    map.value = new mapboxgl.Map({
      container: mapContainer.value,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: center,
      zoom: 15,
    });

    // Update debug data
    debugData.value.mapStatus.initialized = true;
    debugData.value.mapStatus.center = center;

    map.value.addControl(new mapboxgl.NavigationControl(), "top-right");

    // Add drawing tools
    draw.value = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: true,
        trash: true,
      },
      // Add custom styles for polygons
      styles: [
        // Default styles for the polygon (active and inactive)
        {
          id: "gl-draw-polygon-fill-inactive",
          type: "fill",
          filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"]],
          paint: {
            "fill-color": "#0000FF",
            "fill-opacity": 0.3,
          },
        },
        {
          id: "gl-draw-polygon-fill-active",
          type: "fill",
          filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
          paint: {
            "fill-color": "#0000FF",
            "fill-opacity": 0.3,
          },
        },
        // Style for polygon outline (active and inactive)
        {
          id: "gl-draw-polygon-stroke-inactive",
          type: "line",
          filter: ["all", ["==", "active", "false"], ["==", "$type", "Polygon"]],
          layout: {
            "line-cap": "round",
            "line-join": "round",
          },
          paint: {
            "line-color": "#0000FF",
            "line-width": 2,
            "line-opacity": 1,
          },
        },
        {
          id: "gl-draw-polygon-stroke-active",
          type: "line",
          filter: ["all", ["==", "active", "true"], ["==", "$type", "Polygon"]],
          layout: {
            "line-cap": "round",
            "line-join": "round",
          },
          paint: {
            "line-color": "#0000FF",
            "line-width": 2,
            "line-opacity": 1,
          },
        },
        // Styles for the midpoints (vertices/handles)
        {
          id: "gl-draw-polygon-midpoint",
          type: "circle",
          filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
          paint: {
            "circle-radius": 3,
            "circle-color": "#0000FF",
          },
        },
        // Styles for the vertices
        {
          id: "gl-draw-polygon-and-line-vertex-inactive",
          type: "circle",
          filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
          paint: {
            "circle-radius": 5,
            "circle-color": "#FFF",
            "circle-stroke-color": "#0000FF",
            "circle-stroke-width": 2,
          },
        },
        {
          id: "gl-draw-polygon-and-line-vertex-active",
          type: "circle",
          filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["==", "active", "true"]],
          paint: {
            "circle-radius": 6,
            "circle-color": "#FFF",
            "circle-stroke-color": "#0000FF",
            "circle-stroke-width": 3,
          },
        },
      ],
    });

    map.value.addControl(draw.value);

    // Listen for drawing events
    map.value.on("draw.create", updatePropertyBoundary);
    map.value.on("draw.update", updatePropertyBoundary);
    map.value.on("draw.delete", () => {
      hasPolygon.value = false;
      debugData.value.propertyBoundary = null;

      // Update store with null property boundary
      const mapboxData = {
        ...store.formData,
        property_boundary: null,
        property_area_m2: null,
      };
      store.updateMapboxData(mapboxData);
    });

    // If we already have a property boundary, add it to the map
    if (store.formData.property_boundary) {
      try {
        // Try to parse it if it's a string
        const geojsonData =
          typeof store.formData.property_boundary === "string"
            ? JSON.parse(store.formData.property_boundary)
            : store.formData.property_boundary;

        draw.value.add(geojsonData);
        hasPolygon.value = true;
        debugData.value.propertyBoundary = geojsonData;
      } catch (error) {
        console.error("Error adding existing property boundary:", error);
        debugData.value.errors.push(`Error adding existing property boundary: ${error.message}`);
      }
    }
  } catch (error) {
    console.error("Error initializing map:", error);
    debugData.value.errors.push(`Error initializing map: ${error.message}`);
  }
};

// Parse address from Mapbox result
const parseAddress = (result) => {
  const addressParts = {
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
  };

  if (result.context) {
    // Extract city, state, and zip from context
    result.context.forEach((context) => {
      const id = context.id.split(".")[0];
      const text = context.text;

      switch (id) {
        case "place":
          addressParts.city = text;
          break;
        case "region":
          addressParts.state = text;
          break;
        case "postcode":
          addressParts.zip_code = text;
          break;
      }
    });
  }

  // Extract street address
  addressParts.street_address = result.text;
  if (result.address) {
    addressParts.street_address = `${result.address} ${addressParts.street_address}`;
  }

  return addressParts;
};

// Add a ref for property area
const propertyArea = ref(0);

// Update the property boundary when drawing is done
const updatePropertyBoundary = () => {
  try {
    const data = draw.value.getAll();

    // Add styling properties to each polygon feature
    if (data.features && data.features.length > 0) {
      data.features.forEach((feature) => {
        // Only add properties to polygon features
        if (feature.geometry.type === "Polygon") {
          // Add the styling properties
          feature.properties = {
            ...feature.properties,
            stroke: "#0000FF",
            "stroke-width": 2,
            "stroke-opacity": 1,
            fill: "#0000FF",
            "fill-opacity": 0.3,
          };
        }
      });
    }

    debugData.value.propertyBoundary = data;

    if (data.features.length > 0) {
      hasPolygon.value = true;
      // Reset boundary error when polygon is added
      showBoundaryError.value = false;

      // Calculate area if it's a polygon
      if (data.features[0].geometry.type === "Polygon") {
        // Calculate area in square meters
        const area = calculateArea(data.features[0]);
        propertyArea.value = area;

        // Add area to the properties
        data.features[0].properties.area = area;

        // Update the store
        const mapboxData = {
          ...store.formData,
          property_boundary: JSON.stringify(data),
          property_area_m2: area,
        };
        store.updateMapboxData(mapboxData);
      } else {
        // Update the store without area for non-polygon features
        const mapboxData = {
          ...store.formData,
          property_boundary: JSON.stringify(data),
        };
        store.updateMapboxData(mapboxData);
      }
    }
  } catch (error) {
    console.error("Error updating property boundary:", error);
    debugData.value.errors.push(`Error updating property boundary: ${error.message}`);
  }
};

// Calculate area in square meters
const calculateArea = (polygon) => {
  try {
    // Calculate area using Turf.js
    const area = turf.area(polygon);
    return area;
  } catch (error) {
    console.error("Error calculating area:", error);
    return 0;
  }
};

// Format area for display - keep the display formatting separate from the stored value
const formatArea = (area) => {
  if (area < 10000) {
    // Show in square meters if less than 10,000 m²
    return `${Math.round(area)} m²`;
  } else {
    // Show in hectares if larger than 10,000 m²
    return `${(area / 10000).toFixed(2)} hectares (${Math.round(area)} m²)`;
  }
};

// Toggle drawing mode
const toggleDrawingMode = () => {
  isDrawingMode.value = !isDrawingMode.value;

  if (isDrawingMode.value) {
    // Start drawing mode
    draw.value.changeMode("draw_polygon");
  } else {
    // End drawing mode
    draw.value.changeMode("simple_select");
  }
};

// Clear the polygon
const clearPolygon = () => {
  draw.value.deleteAll();
  hasPolygon.value = false;
  propertyArea.value = 0;
  debugData.value.propertyBoundary = null;

  // Show boundary error when polygon is cleared (after already having drawn it)
  showBoundaryError.value = true;

  // Update store with null property boundary
  const mapboxData = {
    ...store.formData,
    property_boundary: null,
    property_area_m2: null,
  };
  store.updateMapboxData(mapboxData);
};

// Method to validate if boundary exists - can be called from parent
const validateBoundary = () => {
  if (!hasPolygon.value && showMap.value) {
    showBoundaryError.value = true;
    return false;
  }
  showBoundaryError.value = false;
  return true;
};

// Handle map refresh when autofill is used
const handleMapRefresh = () => {
  // First, ensure the input field is updated
  if (store.formData.project_location) {
    inputAddress.value = store.formData.project_location;
  }

  // Always make sure map is set to be shown
  showMap.value = true;

  const attemptMapInitialization = (attempts = 0) => {
    // Maximum 3 attempts with exponential backoff
    if (attempts >= 3) {
      console.error("Failed to initialize map after multiple attempts");
      return;
    }

    // Use nextTick to ensure the container is in the DOM
    nextTick(() => {
      // If map is already initialized
      if (map.value && store.formData.latitude && store.formData.longitude) {
        // Update the map position
        map.value.flyTo({
          center: [store.formData.longitude, store.formData.latitude],
          zoom: 15,
        });

        // Clear and re-add the property boundary if provided
        if (draw.value && store.formData.property_boundary) {
          draw.value.deleteAll();

          try {
            // Try to parse it if it's a string
            const geojsonData =
              typeof store.formData.property_boundary === "string"
                ? JSON.parse(store.formData.property_boundary)
                : store.formData.property_boundary;

            draw.value.add(geojsonData);
            hasPolygon.value = true;
            propertyArea.value = store.formData.property_area_m2 || 0;
          } catch (error) {
            console.error("Error adding property boundary during refresh:", error);
          }
        }
      }
      // If map wasn't initialized yet but we have coordinates
      else if (store.formData.latitude && store.formData.longitude) {
        // Check if container is available
        if (!mapContainer.value) {
          // Retry with exponential backoff
          setTimeout(() => attemptMapInitialization(attempts + 1), 100 * Math.pow(2, attempts));
          return;
        }

        // Force re-initialize the map
        if (map.value) {
          map.value.remove();
          map.value = null;
        }

        initializeMap([store.formData.longitude, store.formData.latitude]);
      }
    });
  };

  // Start the initialization process
  attemptMapInitialization();
};

// Expose methods to parent component
defineExpose({
  validateBoundary,
  handleMapRefresh,
});
</script>

<style scoped>
.mapbox-address-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.address-input-container {
  width: 100%;
}

.autocomplete-container {
  position: relative;
  width: 100%;
}

.boundary-requirement {
  margin-bottom: 10px;
}

.suggestions-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #ccc;
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.suggestion-item {
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.suggestion-item:hover {
  background-color: #f5f5f5;
}

.map-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.map-container {
  width: 100%;
  height: 400px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

#map {
  width: 100%;
  height: 100%;
}

.map-controls-container {
  min-height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.map-controls {
  display: flex;
  gap: 10px;
}

.error-message {
  color: red;
  font-size: 12px;
  margin-top: 5px;
}

/* Override Mapbox geocoder styles to match app styling */
:deep(.mapboxgl-ctrl-geocoder) {
  width: 100%;
  max-width: 100%;
  box-shadow: none;
  border: 1px solid #ccc;
  border-radius: 8px;
}

:deep(.mapboxgl-ctrl-geocoder--input) {
  height: 48px;
  padding: 12px 35px 12px 12px;
}

.debug-panel {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #ddd;
  overflow: auto;
  max-height: 300px;
}

.debug-panel h4 {
  margin-top: 0;
  margin-bottom: 10px;
  color: #333;
}

.debug-data {
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
}

.debug-data pre {
  white-space: pre-wrap;
  word-break: break-word;
}

.property-area {
  background-color: #f5f5f5;
  padding: 8px 12px;
  border-radius: 4px;
  font-weight: bold;
}
</style>
